import React, { useState, useEffect, useRef } from 'react';

// Default placeholder as data URL
const DEFAULT_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23f2f4f7'/%3E%3Cpath d='M200 100c-27.614 0-50 22.386-50 50s22.386 50 50 50 50-22.386 50-50-22.386-50-50-50zm0 80c-16.542 0-30-13.458-30-30s13.458-30 30-30 30 13.458 30 30-13.458 30-30 30zm80-60h-40c-2.21 0-4 1.79-4 4s1.79 4 4 4h40c2.21 0 4-1.79 4-4s-1.79-4-4-4zm-160 0h-40c-2.21 0-4 1.79-4 4s1.79 4 4 4h40c2.21 0 4-1.79 4-4s-1.79-4-4-4z' fill='%238c9bab'/%3E%3C/svg%3E";

const LazyImage = ({ 
  src, 
  alt, 
  placeholder = DEFAULT_PLACEHOLDER,
  className = '',
  style = {},
  onLoad,
  onError,
  ...props 
}) => {
  const [imageSrc, setImageSrc] = useState(placeholder);
  const [imageRef, setImageRef] = useState();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const observerRef = useRef();

  useEffect(() => {
    let observer;
    
    if (imageRef && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // Start loading the image
              const img = new Image();
              img.src = src;
              
              img.onload = () => {
                setImageSrc(src);
                setIsLoaded(true);
                if (onLoad) onLoad();
              };
              
              img.onerror = () => {
                setIsError(true);
                if (onError) onError();
              };
              
              // Stop observing once we start loading
              if (observer) {
                observer.unobserve(imageRef);
              }
            }
          });
        },
        {
          rootMargin: '50px',
          threshold: 0.01
        }
      );
      
      observer.observe(imageRef);
      observerRef.current = observer;
    } else {
      // Fallback for browsers without IntersectionObserver
      setImageSrc(src);
      setIsLoaded(true);
    }
    
    return () => {
      if (observer && imageRef) {
        observer.unobserve(imageRef);
      }
    };
  }, [imageRef, src, onLoad, onError]);

  return (
    <img
      ref={setImageRef}
      src={imageSrc}
      alt={alt}
      className={`lazy-image ${className} ${isLoaded ? 'loaded' : 'loading'} ${isError ? 'error' : ''}`}
      style={{
        transition: 'opacity 0.3s ease-in-out',
        opacity: isLoaded ? 1 : 0.8,
        ...style
      }}
      {...props}
    />
  );
};

export default LazyImage; 